import React, { useState, useEffect } from "react";
import FormPageHeader from "../../utilities/FormPageHeader";
import FactoryIssueCard from "./FactoryIssueCard";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import customStyles from "app/shared-components/SelectCustomStyle";
import CustomDatePicker from "../../utilities/CustomDatePicker";
import Select from "react-select";
import pptBackgroundBase64 from "../../../../assets/pptBgBase64";
import moment from "moment";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import OAuthService from "src/app/auth/services/oAuthService";
import { toast } from "react-toastify";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import * as pdfjsLib from "pdfjs-dist";

// pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const pdfToImageBase64 = async (blob, scale = 2) => {
  const pdfData = await blob.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;

  const page = await pdf.getPage(1);

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: context, viewport }).promise;

  return canvas.toDataURL("image/png");
};

const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}`;
const companiesUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.companies}`;
const FactoryIssueNkReport = () => {
  const defaultFromDate = moment()
    .subtract(1, "month")
    .startOf("month")
    .toDate();

  const defaultToDate = moment().subtract(1, "month").endOf("month").toDate();
  const theme = useTheme();
  const [createdDateFrom, setCreatedDateFrom] = useState(defaultFromDate);
  const [createdDateTo, setCreatedDateTo] = useState(defaultToDate);
  const vendorOptions = [
    { value: "NK", label: "NOLTE KUECHEN" },
    { value: "EK", label: "EXPRESS KUECHEN" },
  ];
  const [vendor, setVendor] = useState(vendorOptions[0]);
  const [issues, setIssues] = useState([]);
  const [totalIssues, setTotalIssues] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pptLoading, setPptLoading] = useState(false);
  const [customer, setCustomer] = useState("ALL");
  const formatApiDate = (date) => moment(date).format("YYYY-MM-DD");
  const isImageFile = (fileName) =>
    /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName || "");

  const normalizeIssues = (apiData) =>
    apiData?.map((item, index) => ({
      id: index + 1,
      reason:
        item.reason === "Transportation Damage"
          ? "TRANSPORTATION DAMAGES DUE TO IMPROPER PACKING & STACKING"
          : "WRONG DELIVERY & PRODUCT & PRODUCTION DEFECTS",

      isTransportation: item.reason === "Transportation Damage",

      customerNum: item.customerNum,

      country: `${item.shortKey}-${item.country}`,

      abNumber: item.abNumber,

      articleCode: item?.articleCode || "-",

      createdDate: moment(item.createdDate).format("DD-MM-YYYY"),

      orderDate: item.parentOrderDate
        ? moment(item.parentOrderDate).format("DD-MM-YYYY")
        : null,

      posNo: item.posNo,

      details: item.remark?.trim() || "-",

      picture: item.picture,

      isImage: isImageFile(item.picture),

      selected: true,
    }));

  const groupIssues = (issues) => {
    const grouped = new Map();
    const ungrouped = [];

    issues.forEach((issue) => {
      // Do not group records without AB Number
      if (!issue.abNumber) {
        ungrouped.push({
          ...issue,
          imageData: issue.picture
            ? [
                {
                  picture: issue.picture,
                  isImage: issue.isImage,
                  posNo: issue.posNo,
                  articleCode: issue.articleCode,
                  details: issue.details,
                },
              ]
            : [],
          pictures: issue.isImage && issue.picture ? [issue.picture] : [],
          attachments: !issue.isImage && issue.picture ? [issue.picture] : [],
        });
        return;
      }

      const key = `${issue.abNumber}_${issue.reason}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          ...issue,

          posNos: [issue.posNo],
          articleCodes: [issue.articleCode],
          detailsList: issue.details ? [issue.details] : [],

          pictures: issue.isImage && issue.picture ? [issue.picture] : [],
          attachments: !issue.isImage && issue.picture ? [issue.picture] : [],

          // Store image-specific data
          imageData: issue.picture
            ? [
                {
                  picture: issue.picture,
                  isImage: issue.isImage,
                  posNo: issue.posNo,
                  articleCode: issue.articleCode,
                  details: issue.details,
                },
              ]
            : [],
        });

        return;
      }

      const existing = grouped.get(key);

      if (issue.posNo) {
        existing.posNos.push(issue.posNo);
      }

      if (issue.articleCode) {
        existing.articleCodes.push(issue.articleCode);
      }

      if (issue.details) {
        existing.detailsList.push(issue.details);
      }

      if (issue.picture) {
        if (issue.isImage) {
          existing.pictures.push(issue.picture);
        } else {
          existing.attachments.push(issue.picture);
        }

        existing.imageData.push({
          picture: issue.picture,
          isImage: issue.isImage,
          posNo: issue.posNo,
          articleCode: issue.articleCode,
          details: issue.details,
        });
      }
    });

    const groupedResults = Array.from(grouped.values()).map((item, index) => ({
      ...item,
      id: index + 1,

      posNo: [...new Set(item.posNos)].join(", "),

      articleCode: [...new Set(item.articleCodes)].filter(Boolean).join(", "),

      details: [...new Set(item.detailsList)]
        .filter(Boolean)
        .map((x) => `• ${x}`)
        .join("\n"),

      pictures: [...new Set(item.pictures)],
      attachments: [...new Set(item.attachments)],

      selected: true,
    }));

    return [...groupedResults, ...ungrouped].map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  };
  const fetchFactoryIssues = async () => {
    if (!vendor || !createdDateFrom || !createdDateTo) return;

    const startDate = formatApiDate(createdDateFrom);
    const endDate = formatApiDate(createdDateTo);

    setLoading(true);

    try {
      const response = await OAuthService.addData(
        `${url}api/get-factory-issues/${vendor.value}?startDate=${startDate}&endDate=${endDate}`,
      );

      const groupedIssues = groupIssues(normalizeIssues(response.data) || []);

      setTotalIssues(groupedIssues);

      if (customer !== "ALL") {
        setIssues(
          groupedIssues.filter((issue) => issue.customerNum === customer),
        );
      } else {
        setIssues(groupedIssues);
      }
    } catch (error) {
      toast.error(error?.response?.data?.errorMessage || error.message);
      setTotalIssues([]);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };
  const handleSelectCard = (id, checked) => {
    setIssues((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: checked } : item,
      ),
    );

    setTotalIssues((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: checked } : item,
      ),
    );
  };
  useEffect(() => {
    if (createdDateFrom && createdDateTo && createdDateTo < createdDateFrom) {
      setCreatedDateTo(null);
    }
  }, [createdDateFrom, createdDateTo]);
  useEffect(() => {
    if (vendor && createdDateFrom && createdDateTo) fetchFactoryIssues();
  }, [vendor, createdDateFrom, createdDateTo]);
  const handleSelectChange = (data, key) => {
    setCustomer(data ? data.value : "ALL");
  };

  useEffect(() => {
    if (customer !== "ALL") {
      let filteredIssues = totalIssues.filter(
        (issue) => issue.customerNum === customer,
      );
      setIssues(filteredIssues);
    } else {
      fetchFactoryIssues();
    }
  }, [customer]);

  const getCustomer = async () => {
    try {
      const list = await OAuthService.getList(
        companiesUrl +
          `/search/findActiveByCustomerTypes?&customerTypes=REGULAR&customerTypes=WHOLESALE&page=0&sort=id,ase,&size=1000`,
      );
      let companies = list.data?._embedded?.companies;
      companies = companies.map((company) => {
        return {
          value: company.id,
          label: `${company?.id}-${company?.shortKey}-${company?.countryName}`,
        };
      });
      companies = [{ value: "ALL", label: "ALL" }, ...companies];
      setCustomerOptions(companies);
    } catch (error) {
      toast.error(error.message);
    }
  };
  useEffect(() => {
    getCustomer();
  }, []);
  const handleVendorChange = (selectedOption) => {
    setVendor(selectedOption);
  };
  const handleDetailsChange = (id, value) => {
    setIssues((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              imageData: value,
            }
          : item,
      ),
    );

    setTotalIssues((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              imageData: value,
            }
          : item,
      ),
    );
  };
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  const getImageSizeFromBlob = (blob) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(blob);
    });
  const vendorTitle = vendor?.label || "";

  const formattedFromDate = createdDateFrom
    ? moment(createdDateFrom).format("DD.MM.YYYY")
    : "";

  const formattedToDate = createdDateTo
    ? moment(createdDateTo).format("DD.MM.YYYY")
    : "";

  const isPdfFile = (fileName) => /\.pdf$/i.test(fileName);
  const getFileType = (fileName) =>
    fileName?.split(".").pop()?.toUpperCase() || "FILE";

  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const handleDownloadPPT = async () => {
    setPptLoading(true);

    try {
      const pptx = new window.PptxGenJS();

      pptx.defineSlideMaster({
        title: "MASTER_BG",
        background: { data: pptBackgroundBase64 },
      });

      /* ===========================
       TITLE SLIDE
    ============================ */
      const titleSlide = pptx.addSlide({ masterName: "MASTER_BG" });

      titleSlide.addText(vendorTitle, {
        x: 0,
        y: 1.8,
        w: "100%",
        align: "center",
        fontSize: 32,
        bold: true,
      });

      titleSlide.addText("FACTORY RELATED ISSUES", {
        x: 0,
        y: 2.6,
        w: "100%",
        align: "center",
        fontSize: 26,
        bold: true,
      });

      titleSlide.addText(`(${formattedFromDate}  to  ${formattedToDate})`, {
        x: 0,
        y: 3.4,
        w: "100%",
        align: "center",
        fontSize: 18,
      });

      const selectedIssues = issues.filter((i) => i.selected);

      /* ===========================
       ISSUE SLIDES
    ============================ */
      for (const item of selectedIssues) {
        const imageGroups = chunkArray(item.imageData || [], 4);
        const groups = imageGroups.length ? imageGroups : [[]];

        const positions = [
          { x: 5.0, y: 1.0 },
          { x: 7.0, y: 1.0 },
          { x: 5.0, y: 3.0 },
          { x: 7.0, y: 3.0 },
        ];

        for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
          const slide = pptx.addSlide({ masterName: "MASTER_BG" });

          /* ================= HEADER ================= */
          const headerColor = item.isTransportation ? "FFED00" : "00E5B0";

          slide.addShape(pptx.ShapeType.rect, {
            x: 0.3,
            y: 0.3,
            w: 9.4,
            h: 0.4,
            fill: { color: headerColor },
          });

          slide.addText(item.reason, {
            x: 0.3,
            y: 0.3,
            w: 9.4,
            h: 0.4,
            fontSize: 18,
            bold: true,
            color: "000000",
            align: "center",
            valign: "middle",
          });
          const currentImages = groups[groupIndex];
          const currentPosNos = [
            ...new Set(currentImages.map((img) => img.posNo).filter(Boolean)),
          ].join(", ");

          const currentArticleCodes = [
            ...new Set(
              currentImages.map((img) => img.articleCode).filter(Boolean),
            ),
          ].join(", ");
          /* ================= TABLE ================= */
          slide.addShape(pptx.ShapeType.rect, {
            x: 0.4,
            y: 1.2,
            w: 3.6,
            h: item.orderDate ? 1.68 : 1.4,
            line: { color: "000000", width: 1 },
            fill: { color: "FFFFFF" },
          });

          slide.addTable(
            [
              [
                { text: "Country", options: { fill: "FFD966", wrap: false } },
                {
                  text: `${item.country}`,
                  options: { bold: true, fill: "FFFF99" },
                },
              ],
              [
                {
                  text: "Order Placed Date",
                  options: { fill: "FFD966", wrap: false },
                },
                {
                  text: `${item.createdDate}`,
                  options: { bold: true, fill: "FFFF99" },
                },
              ],

              ...(item.orderDate
                ? [
                    [
                      {
                        text: "Org Order Loading Date",
                        options: { fill: "FFD966", wrap: false },
                      },
                      {
                        text: item.orderDate,
                        options: { bold: true, fill: "FFFF99" },
                      },
                    ],
                  ]
                : []),
              [
                { text: "AB Number", options: { fill: "FFD966", wrap: false } },
                {
                  text: item.abNumber,
                  options: { bold: true, fill: "FFFF99" },
                },
              ],
              [
                {
                  text: "Article Code",
                  options: { fill: "FFD966", wrap: false },
                },
                {
                  text: `${currentArticleCodes}`,
                  options: { bold: true, fill: "FFFF99" },
                },
              ],
              [
                {
                  text: "Pos. Number",
                  options: { fill: "FFD966", wrap: false },
                },
                {
                  text: currentPosNos,
                  options: { bold: true, fill: "FFFF99" },
                },
              ],
            ],
            {
              x: 0.4,
              y: 1.2,
              w: 3.6,
              colW: [1.8, 1.8],
              fontSize: 10,
              rowH: 0.28,
              border: { type: "none" },
            },
          );

          /* ================= DETAILS ================= */
          slide.addShape(pptx.ShapeType.rect, {
            x: 0.4,
            y: 3.0,
            w: 3.6,
            h: 1.7,
            line: { color: "000000", width: 1 },
            fill: { color: "FFFF99" },
          });

          slide.addText("Details :", {
            x: 0.5,
            y: 3.1,
            fontSize: 12,
            bold: true,
          });

          const detailsText =
            currentImages.length > 0
              ? currentImages
                  .map(
                    (img) => `Pos ${img.posNo || "-"} - ${img.details || "-"}`,
                  )
                  .join("\n")
              : "-";

          const fontSize = detailsText.length > 300 ? 7 : 10;

          slide.addText(detailsText, {
            x: 0.5,
            y: 3.6,
            w: 3.0,
            fontSize,
          });

          const layouts = {
            1: [
              {
                x: 4.6,
                y: 1.0,
                w: 4.8,
                h: 3.8,
              },
            ],

            2: [
              {
                x: 4.4,
                y: 1.0,
                w: 2.4,
                h: 3.8,
              },
              {
                x: 7.0,
                y: 1.0,
                w: 2.4,
                h: 3.8,
              },
            ],

            3: [
              {
                x: 4.4,
                y: 1.0,
                w: 2.4,
                h: 1.8,
              },
              {
                x: 7.0,
                y: 1.0,
                w: 2.4,
                h: 1.8,
              },
              {
                x: 5.7,
                y: 3.0,
                w: 2.4,
                h: 1.8,
              },
            ],

            4: [
              {
                x: 4.4,
                y: 1.0,
                w: 2.4,
                h: 1.8,
              },
              {
                x: 7.0,
                y: 1.0,
                w: 2.4,
                h: 1.8,
              },
              {
                x: 4.4,
                y: 3.0,
                w: 2.4,
                h: 1.8,
              },
              {
                x: 7.0,
                y: 3.0,
                w: 2.4,
                h: 1.8,
              },
            ],
          };
          const imageLayout = layouts[currentImages.length] || layouts[4];
          if (groups.length > 1) {
            slide.addText(`Slide ${groupIndex + 1}/${groups.length}`, {
              x: 7,
              y: 0.8,
              w: 2,
              align: "right",
              fontSize: 8,
            });
          }

          for (let i = 0; i < currentImages.length; i++) {
            const imageInfo = currentImages[i];

            const picture = imageInfo.picture;
            const pos = imageLayout[i];
            const downloadUrl = `${url}api/static/downloadPicture?picture=${encodeURIComponent(
              picture,
            )}`;

            try {
              const response = await OAuthService.getDownload(downloadUrl);
              const blob = response?.data;

              if (!blob) continue;
              slide.addText(`POS : ${imageInfo.posNo || "-"}`, {
                x: pos.x,
                y: pos.y - 0.2,
                w: pos.w,
                h: 0.18,
                align: "center",
                fontSize: 8,
                bold: true,
              });
              if (isImageFile(picture)) {
                try {
                  const response = await OAuthService.getDownload(downloadUrl);
                  const blob = response.data;

                  if (blob) {
                    const base64Image = await blobToBase64(blob);
                    const { width, height } = await getImageSizeFromBlob(blob);

                    const maxW = pos.w;
                    const maxH = pos.h;

                    const imageRatio = width / height;
                    const boxRatio = maxW / maxH;

                    let finalW, finalH;

                    if (imageRatio > boxRatio) {
                      finalW = maxW;
                      finalH = maxW / imageRatio;
                    } else {
                      finalH = maxH;
                      finalW = maxH * imageRatio;
                    }

                    slide.addImage({
                      data: base64Image,
                      x: pos.x + (maxW - finalW) / 2,
                      y: pos.y + (maxH - finalH) / 2,
                      w: finalW,
                      h: finalH,
                    });
                  }
                } catch (err) {
                  console.error(err);
                }
              } else if (isPdfFile(picture)) {
                try {
                  const response = await OAuthService.getDownload(downloadUrl);
                  const blob = response.data;

                  if (blob) {
                    const base64PdfImage = await pdfToImageBase64(blob);

                    const img = new Image();
                    img.src = base64PdfImage;
                    await new Promise((resolve) => (img.onload = resolve));

                    const maxW = pos.w;
                    const maxH = pos.h;

                    const imageRatio = img.width / img.height;
                    const boxRatio = maxW / maxH;

                    let finalW, finalH;

                    if (imageRatio > boxRatio) {
                      finalW = maxW;
                      finalH = maxW / imageRatio;
                    } else {
                      finalH = maxH;
                      finalW = maxH * imageRatio;
                    }

                    slide.addImage({
                      data: base64PdfImage,
                      x: pos.x + (maxW - finalW) / 2,
                      y: pos.y + (maxH - finalH) / 2,
                      w: finalW,
                      h: finalH,
                    });
                  }
                } catch (err) {
                  console.error(err);
                  slide.addText(
                    [
                      {
                        text: "📄 Download PDF",
                        options: {
                          bold: true,
                          color: "0070C0",
                          hyperlink: {
                            url: downloadUrl,
                            tooltip: picture,
                          },
                        },
                      },
                    ],
                    {
                      x: pos.x,
                      y: pos.y + 0.8,
                      w: pos.w,
                      align: "center",
                    },
                  );

                  slide.addText(picture, {
                    x: pos.x,
                    y: pos.y + 1.2,
                    w: pos.w,
                    fontSize: 8,
                    align: "center",
                  });
                }
              } else {
                const fileType = getFileType(picture);

                slide.addText(
                  [
                    {
                      text: "📎 ",
                      options: {
                        fontSize: 16,
                      },
                    },
                    {
                      text: `Download Attachment (${fileType})`,
                      options: {
                        bold: true,
                        color: "0070C0",
                        fontSize: 10,
                        hyperlink: {
                          url: downloadUrl,
                          tooltip: picture,
                        },
                      },
                    },
                  ],
                  {
                    x: pos.x,
                    y: pos.y + 0.8,
                    w: pos.w,
                    align: "center",
                  },
                );

                slide.addText(picture, {
                  x: pos.x,
                  y: pos.y + 1.2,
                  w: pos.w,
                  align: "center",
                  fontSize: 8,
                  color: "666666",
                  breakLine: true,
                });
              }
            } catch (err) {
              console.error("File load failed:", err);
            }
          }
        }
      }

      /* ===========================
       THANK YOU SLIDE
    ============================ */
      const thankYouSlide = pptx.addSlide({ masterName: "MASTER_BG" });

      thankYouSlide.addText("THANK YOU", {
        x: 0,
        y: 2.5,
        w: "100%",
        align: "center",
        fontSize: 36,
        bold: true,
      });

      await pptx.writeFile({
        fileName: `${vendorTitle}_Factory_Issues_${formattedFromDate}_to_${formattedToDate}.pptx`,
      });
    } catch (err) {
      console.error("PPT generation failed:", err);
    } finally {
      setPptLoading(false);
    }
  };
  return (
    <>
      <FormPageHeader
        componentName={`Factory Related Issues - ${vendor?.label}`}
        pageName={`Factory Related Issues - ${vendor?.label}`}
        currentPage={`Factory Related Issues - ${vendor?.label}`}
      >
        <Box className="flex pt-8 flex-col px-16 sm:px-24 mt-4" sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "1fr 1fr 1fr 1fr auto",
              },
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <Select
              placeholder="Vendor"
              name="vendorId"
              value={vendor}
              options={vendorOptions}
              onChange={handleVendorChange}
              styles={customStyles()}
            />

            <Select
              placeholder={"Customer"}
              name="customerId"
              label={"Customer"}
              value={
                customer &&
                customerOptions &&
                customerOptions.filter((obj) => customer === obj.value)
              }
              options={customerOptions}
              onChange={(e) => handleSelectChange(e)}
              styles={customStyles()}
              isClearable
            />

            <CustomDatePicker
              label="Created From"
              format={"dd-MM-yyyy"}
              name={"createdDateFrom"}
              value={createdDateFrom || ""}
              maxDate={new Date()}
              handleChange={(date) => setCreatedDateFrom(date)}
              componentsProps={{ actionBar: { actions: ["clear"] } }}
            />

            <CustomDatePicker
              label="Created To"
              format={"dd-MM-yyyy"}
              name={"createdDateTo"}
              value={createdDateTo || ""}
              minDate={createdDateFrom}
              maxDate={new Date()}
              handleChange={(date) => setCreatedDateTo(date)}
              componentsProps={{ actionBar: { actions: ["clear"] } }}
            />

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="Download Factory Issue Report">
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{ minWidth: "auto" }}
                  onClick={handleDownloadPPT}
                  disabled={loading || issues.length === 0 || pptLoading}
                  style={{
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                  }}
                >
                  Download
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </FormPageHeader>

      <div className="px-4 sm:px-12 md:px-48 pt-12 md:pt-24">
        <Box
          sx={{
            height: { xs: "auto", md: "100vh" },
            overflowY: { xs: "visible", md: "auto" },
            px: { xs: 1, md: 6 },
            pt: { xs: 1, md: 3 },
          }}
        >
          <Grid container spacing={3} justifyContent="center">
            {loading && (
              <div className="flex justify-center py-24">
                <CircularProgress color="primary" size={18} />
              </div>
            )}

            {!loading &&
              (issues?.length > 0 ? (
                <>
                  {issues.map((item) => (
                    <Grid
                      item
                      xs={12}
                      key={item.id}
                      sx={{ height: { xs: "auto", md: 420 } }}
                    >
                      <FactoryIssueCard
                        item={item}
                        onDetailsChange={handleDetailsChange}
                        onSelectChange={handleSelectCard}
                      />
                    </Grid>
                  ))}
                </>
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "40vh",
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      No data available for the selected date period
                    </Typography>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </Box>
      </div>
    </>
  );
};

export default FactoryIssueNkReport;
